// Permissions tab (IT only; SPEC.md, Settings and permissions): who can do what
// in IT Budget Tools (it_role_permissions). A row names a role and grants one
// capability an access level; each level includes the ones before it. Access
// is granted by role only, and only takes effect for a role that also has the
// IT Budget tool (Role Console); such rows are flagged. Rows are edited in
// place, and new rows are added at the bottom of the list.

// REACT IMPORTS
import React, { useCallback, useEffect, useMemo, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { CircularProgress, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// CUSTOM COMPONENTS
import { SortableHeaderCell, useSortedRows } from "../../Admin/TimeOffConsole/sortableTable";

// API, CONSTANTS
import { deleteAPICall, getAPICall, patchAPICall, postAPICall } from "../../../config/apiCalls";
import { apiError } from "../itBudgetUtils";

const BLANK = { role_id: "", capability: "", access: "view" };
// Alphabetical sorting on every column (text sorts A→Z first)
const SORT_GETTERS = { role: (p) => p.role_name || "", capability: (p) => p.capability, access: (p) => p.access };

const PermissionsTab = ({ lookups }) => {
	const { instance, accounts } = useMsal();
	const [permissions, setPermissions] = useState([]);
	const [editingId, setEditingId] = useState(null);
	const [draft, setDraft] = useState(BLANK); // the row being edited
	const [adding, setAdding] = useState(BLANK); // the add row at the bottom
	const [busy, setBusy] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setPermissions((await getAPICall(instance, accounts[0], "/api/it-budget/permissions")).data);
		} catch (err) {
			setError(apiError(err, "Couldn't load permissions."));
		} finally {
			setLoading(false);
		}
	}, [instance, accounts]);

	useEffect(() => {
		load();
	}, [load]);

	const { sorted, sortKey, sortDir, onSort } = useSortedRows(permissions, SORT_GETTERS, "role", "asc");

	// Run a change; the server answers with the whole list.
	const run = async (fn, after) => {
		setBusy(true);
		setError("");
		try {
			setPermissions((await fn()).data.permissions);
			after?.();
		} catch (err) {
			setError(apiError(err, "Couldn't save the permission."));
		} finally {
			setBusy(false);
		}
	};

	const startEdit = (p) => {
		setError("");
		setEditingId(p.id);
		setDraft({ role_id: p.role_id, capability: p.capability, access: p.access });
	};
	const saveEdit = () =>
		run(() => patchAPICall(instance, accounts[0], `/api/it-budget/permissions/${editingId}`, { ...draft, role_id: Number(draft.role_id) }), () => setEditingId(null));
	const remove = (id) => run(() => deleteAPICall(instance, accounts[0], `/api/it-budget/permissions/${id}`));
	const add = () =>
		run(() => postAPICall(instance, accounts[0], "/api/it-budget/permissions", { ...adding, role_id: Number(adding.role_id) }), () => setAdding(BLANK));

	const roleOptions = useMemo(() => lookups.roles || [], [lookups]);
	const selects = (value, setValue) => (
		<>
			<TableCell>
				<TextField select size="small" fullWidth label="Role" value={value.role_id} onChange={(e) => setValue({ ...value, role_id: e.target.value })}>
					{roleOptions.map((r) => (
						<MenuItem key={r.id} value={r.id}>
							{r.name}
						</MenuItem>
					))}
				</TextField>
			</TableCell>
			<TableCell>
				<TextField select size="small" fullWidth label="Capability" value={value.capability} onChange={(e) => setValue({ ...value, capability: e.target.value })}>
					{(lookups.capabilities || []).map((c) => (
						<MenuItem key={c} value={c}>
							{c}
						</MenuItem>
					))}
				</TextField>
			</TableCell>
			<TableCell>
				<TextField select size="small" fullWidth label="Access" value={value.access} onChange={(e) => setValue({ ...value, access: e.target.value })}>
					{(lookups.accessLevels || []).map((a) => (
						<MenuItem key={a} value={a}>
							{a}
						</MenuItem>
					))}
				</TextField>
			</TableCell>
		</>
	);

	if (loading) {
		return (
			<div className="itBudgetEmpty">
				<CircularProgress />
			</div>
		);
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<div className="itBudgetBanner">
				<InfoIcon fontSize="small" style={{ color: "#184c93" }} />
				<span>
					IT only. Access is granted by role: everyone with a role gets that role's access. A role first needs the IT Budget tool (Admin › Role Console) to
					open IT Budget at all; these rows then set what it can do. Levels are ordered view, export, record, approve, full; each
					includes the ones before it. A change applies within a minute. Cardholders complete their own statements through the card list, not from here, and
					the Administrator's settings permission can't be changed or removed.
				</span>
			</div>
			{error && <div className="itBudgetError">{error}</div>}
			<div className="itBudgetTableContainer">
				<Table size="small">
					<TableHead>
						<TableRow>
							<SortableHeaderCell sortKey="role" activeKey={sortKey} dir={sortDir} onSort={onSort}>
								Role
							</SortableHeaderCell>
							<SortableHeaderCell sortKey="capability" activeKey={sortKey} dir={sortDir} onSort={onSort}>
								Capability
							</SortableHeaderCell>
							<SortableHeaderCell sortKey="access" activeKey={sortKey} dir={sortDir} onSort={onSort}>
								Access
							</SortableHeaderCell>
							<TableCell align="right" sx={{ width: 110 }} />
						</TableRow>
					</TableHead>
					<TableBody>
						{sorted.map((p) =>
							editingId === p.id ? (
								<TableRow key={p.id} className="itBudgetSelectedRow">
									{selects(draft, setDraft)}
									<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
										<Tooltip title="Save">
											<span>
												<IconButton size="small" color="primary" aria-label="Save permission" disabled={busy} onClick={saveEdit}>
													{busy ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title="Cancel">
											<IconButton size="small" aria-label="Cancel" disabled={busy} onClick={() => setEditingId(null)}>
												<CloseIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							) : (
								<TableRow key={p.id}>
									<TableCell>
									<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
										{p.role_name}
										{!p.has_tool && (
											<Tooltip title="This role doesn't have the IT Budget tool, so it can't open IT Budget. Add the tool to the role in Admin › Role Console.">
												<WarningAmberIcon fontSize="small" style={{ color: "#b45309" }} aria-label="No IT Budget page access" />
											</Tooltip>
										)}
									</span>
								</TableCell>
									<TableCell className="itBudgetMono">{p.capability}</TableCell>
									<TableCell>{p.access}</TableCell>
									<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
										{p.capability === "settings" ? (
											<Tooltip title="The Administrator's settings permission can't be changed or removed">
												<LockIcon fontSize="small" style={{ color: "#9e9e9e", marginRight: 8 }} />
											</Tooltip>
										) : (
											<>
												<Tooltip title="Edit">
													<span>
														<IconButton size="small" aria-label={`Edit ${p.role_name} ${p.capability}`} disabled={busy || editingId !== null} onClick={() => startEdit(p)}>
															<EditIcon fontSize="small" />
														</IconButton>
													</span>
												</Tooltip>
												<Tooltip title="Remove">
													<span>
														<IconButton size="small" aria-label={`Remove ${p.role_name} ${p.capability}`} disabled={busy || editingId !== null} onClick={() => remove(p.id)}>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</span>
												</Tooltip>
											</>
										)}
									</TableCell>
								</TableRow>
							),
						)}
						{/* Add a permission: the last row of the list */}
						<TableRow>
							{selects(adding, setAdding)}
							<TableCell align="right">
								<Tooltip title="Add permission">
									<span>
										<IconButton color="primary" aria-label="Add permission" disabled={busy || !adding.role_id || !adding.capability} onClick={add}>
											<AddIcon />
										</IconButton>
									</span>
								</Tooltip>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</div>
		</div>
	);
};

export default PermissionsTab;
